import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import Logo from '@/components/Logo'
import AuthBackgroundShape from '@/components/AuthBackgroundShape'
import { supabase } from '@/lib/supabase'

const MAX_RETRIES = 3
const RETRY_DELAY = 500

const CompleteRegistrationPage = () => {
  const [isValidSession, setIsValidSession] = useState(false)
  const [isChecking, setIsChecking] = useState(true)
  const navigate = useNavigate()
  const hasProcessed = useRef(false)

  useEffect(() => {
    if (hasProcessed.current) return
    hasProcessed.current = true
    
    let cancelled = false

    const processEmailConfirmation = async () => {
      // Wait for Supabase to process the email confirmation token
      // The token is in the URL hash and Supabase auto-processes it
      for (let i = 0; i < MAX_RETRIES; i++) {
        const { data: { session } } = await supabase.auth.getSession()
        
        if (cancelled) return
        
        if (session?.access_token) {
          setIsValidSession(true)
          setIsChecking(false)
          await supabase.auth.signOut()
          return
        }
        
        if (i < MAX_RETRIES - 1) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY))
        }
      }
      
      if (!cancelled) {
        navigate('/auth/login')
        setIsChecking(false)
      }
    }

    processEmailConfirmation()

    return () => {
      cancelled = true
      hasProcessed.current = false
    }
  }, [navigate])

  if (isChecking) {
    return (
      <div className='relative flex h-auto min-h-screen items-center justify-center overflow-x-hidden px-4 py-10 sm:px-6 lg:px-8'>
        <div className='absolute'>
          <AuthBackgroundShape />
        </div>

        <Card className='z-1 w-full border-none shadow-md sm:max-w-lg'>
          <CardContent className='pt-6'>
            <p className='text-muted-foreground text-center'>Verifying email confirmation...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!isValidSession) {
    return null
  }

  return (
    <div className='relative flex h-auto min-h-screen items-center justify-center overflow-x-hidden px-4 py-10 sm:px-6 lg:px-8'>
      <div className='absolute'>
        <AuthBackgroundShape />
      </div>

      <Card className='z-1 w-full border-none shadow-md sm:max-w-lg'>
        <CardHeader className='gap-6'>
          <div className='flex justify-center'>
            <Logo className='gap-3' showText={false} />
          </div>

          <div className='flex flex-col items-center text-center'>
            <CardTitle className='mb-1.5 text-2xl'>Email confirmed</CardTitle>
            <CardDescription className='text-base'>
              Your account has been successfully verified. You can now sign in.
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className='space-y-4'>
          <Button className='w-full' onClick={() => navigate('/auth/login')}>
            Sign in
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

export default CompleteRegistrationPage
